import React, { useState } from "react";
import axios from "axios";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { BasicForm } from "@components/shared";

export default function ProfileForm({ id, profile = null, onSuccess }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  function onSubmit(formData) {
    setError(null);
    setLoading(true);

    if (id) {
      return onUpdate(formData);
    }

    return onCreate(formData);
  }

  function onCreate(formData) {
    const postData = { ...formData };

    axios
      .post(`/occupational-profiles/`, postData)
      .then(({ data }) => {
        setLoading(false);
        toast.success(t("created"));
        onSuccess(data.response?.[0]);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  function onUpdate(formData) {
    const postData = {
      ...formData,
    };

    axios
      .put(`/occupational-profiles/${id}/`, postData)
      .then(() => {
        setLoading(false);
        toast.success(t("updated"));
        onSuccess();
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  const fields = [
    {
      name: "original_name",
      label: t("name"),
      initialValue: profile?.original_name || "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "holland",
      label: "Holland",
      initialValue: profile?.holland || "",
      required: true,
      textarea: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "source_year",
      label: "Source Year",
      initialValue: profile?.source_year || "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "cumulative_sd",
      label: "Cumulative SD",
      initialValue: profile?.cumulative_sd || "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "ciss",
      label: "CISS",
      initialValue: profile?.ciss || "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "mbti",
      label: "MBTI",
      initialValue: profile?.mbti || "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "area",
      label: "Area",
      initialValue: profile?.area || "",
      required: true,
      textarea: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "an",
      label: "AN",
      initialValue: profile?.an || "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "ex",
      label: "EX",
      initialValue: profile?.ex || "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "in",
      label: "IN",
      initialValue: profile?.in || "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "sc",
      label: "SC",
      initialValue: profile?.sc || "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "tm",
      label: "TM",
      initialValue: profile?.tm || "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "a",
      label: "A",
      initialValue: profile?.a || "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "b",
      label: "B",
      initialValue: profile?.b || "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "c",
      label: "C",
      initialValue: profile?.c || "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "e",
      label: "E",
      initialValue: profile?.e || "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "f",
      label: "F",
      initialValue: profile?.f || "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "g",
      label: "G",
      initialValue: profile?.g || "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "h",
      label: "H",
      initialValue: profile?.h || "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "i",
      label: "I",
      initialValue: profile?.i || "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "l",
      label: "L",
      initialValue: profile?.l || "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "m",
      label: "M",
      initialValue: profile?.m || "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "n",
      label: "N",
      initialValue: profile?.n || "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "o",
      label: "O",
      initialValue: profile?.o || "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "q1",
      label: "Q1",
      initialValue: profile?.q1 || "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "q2",
      label: "Q2",
      initialValue: profile?.q2 || "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "q3",
      label: "Q3",
      initialValue: profile?.q3 || "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "q4",
      label: "Q4",
      initialValue: profile?.q4 || "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "hol",
      label: "Heart of Leadership Profile",
      required: false,
      radio: true,
      initialValue: profile?.hol || "",
      schema: () => null,
    },
  ].filter((f) =>
    id ? f.name !== "hol" : ["original_name", "holland"].includes(f.name)
  );

  return (
    <BasicForm
      buttonText="Save"
      error={error}
      fields={fields}
      loading={loading}
      onSubmit={onSubmit}
      buttonProps={{
        fullWidth: id ? false : true,
      }}
    />
  );
}

// ProfileForm.defaultProps = {
//   anValue: "",
//   area: "",
//   aValue: "",
//   bValue: "",
//   ciss: "",
//   cValue: "",
//   cumulativeSd: "",
//   eValue: "",
//   exValue: "",
//   fValue: "",
//   gValue: "",
//   hol: false,
//   holland: "",
//   hValue: "",
//   id: null,
//   inValue: "",
//   iValue: "",
//   lValue: "",
//   mbti: "",
//   mValue: "",
//   name: "",
//   nValue: "",
//   oValue: "",
//   q1Value: "",
//   q2Value: "",
//   q3Value: "",
//   q4Value: "",
//   scValue: "",
//   sourceYear: "",
//   tmValue: "",
// };
